import {
  Component,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ModalOptions, ProfileEntity } from '@meplis/common';
import { ModalComponent } from '../../common/modal/modal.component';
import { AlertNoticeComponent } from '../../common/alert-notice/alert-notice.component';
import { MyProfileComponent } from 'apps/meplis-cnp/src/app/pages/profile/components/my-profile/my-profile.component';
import { ProfileCompletenessComponent } from '../../common/profile/profile-completeness/profile-completeness.component';
import { ProfileService } from '@cnp/services';
import { Subscription, switchMap } from 'rxjs';

/** What the profile is missing, so the modal can say so instead of just
 * appearing. */
export interface ProfileModalData {
  /** Fields the platform cannot work without -- the modal is not dismissable. */
  missingRequired?: boolean;
  /** A photo. Nice to have, never a reason to trap someone in a dialog. */
  missingAvatar?: boolean;
}

@Component({
  selector: 'app-profile-modal',
  imports: [
    ModalComponent,
    MyProfileComponent,
    AlertNoticeComponent,
    ProfileCompletenessComponent,
  ],
  templateUrl: './profile-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './profile-modal.component.scss',
})
export class ProfileModalComponent implements OnDestroy {
  private _translateService = inject(TranslateService);
  private _profileService = inject(ProfileService);
  dialogRef = inject<MatDialogRef<ProfileModalComponent>>(MatDialogRef);

  public modalOptions: ModalOptions;
  public isSaving = false;
  public profile: ProfileEntity | null = null;

  private _profileSubscription: Subscription;
  private _profileChangedSubscription: Subscription;

  public readonly missingRequired: boolean;
  public readonly missingAvatar: boolean;

  @ViewChild('myProfile')
  public myProfileComponent!: MyProfileComponent;

  constructor() {
    const data = inject<ProfileModalData>(MAT_DIALOG_DATA, { optional: true });

    this.missingRequired = data?.missingRequired ?? false;
    this.missingAvatar = data?.missingAvatar ?? false;

    this._profileSubscription = this._profileService
      .getProfile()
      .subscribe((profile) => (this.profile = profile));

    this._profileChangedSubscription = this._profileService.onProfileChanged
      .pipe(switchMap(() => this._profileService.getProfile()))
      .subscribe((profile) => (this.profile = profile));

    this.modalOptions = {
      title: this._translateService.instant('PROFILE.TITLE'),
      showPositiveButton: true,
      positiveButtonLabel: this._translateService.instant('SAVE'),
      // Only the required fields justify holding someone here. A missing photo
      // is a suggestion, and a dialog with no way out reads as a bug.
      closeable: !this.missingRequired,
    };
  }

  /** The line that says what is actually missing. */
  public get noticeKey(): string | null {
    if (this.missingRequired && this.missingAvatar) {
      return 'PROFILE.MISSING_REQUIRED_AND_AVATAR';
    }
    if (this.missingRequired) {
      return 'PROFILE.MISSING_REQUIRED';
    }
    if (this.missingAvatar) {
      return 'PROFILE.MISSING_AVATAR';
    }
    return null;
  }

  public ngOnDestroy(): void {
    this._profileSubscription?.unsubscribe();
    this._profileChangedSubscription?.unsubscribe();
  }

  public closeModal(): void {
    this.dialogRef.close();
  }

  public async saveProfile(): Promise<void> {
    this.isSaving = true;
    const saved = await this.myProfileComponent.saveProfile();
    this.isSaving = false;

    if (saved) {
      this.closeModal();
    }
  }
}
